import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Link,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { Table, TableColumn } from '@backstage/core-components';
import { RollbackOptionsValue } from './types';
import { parseDateFromStringSeconds } from './utils';

export const CloudbuildRollbackTableView: React.FC<{
  rollbackOptions: Record<string, RollbackOptionsValue[]> | null;
  onSelectionChange?: (selected: RollbackOptionsValue[]) => void;
}> = ({ rollbackOptions, onSelectionChange }) => {
  const [selectedRows, setSelectedRows] = useState<RollbackOptionsValue[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');

  useEffect(() => {
    // If rollbackOptions changes and we have environments, set the default selected environment
    if (rollbackOptions && Object.keys(rollbackOptions).length > 0) {
      // Set the first environment as default if no environment is selected yet
      if (!selectedEnvironment) {
        setSelectedEnvironment(Object.keys(rollbackOptions)[0]);
      }
    }
  }, [rollbackOptions, selectedEnvironment]);

  useEffect(() => {
    // Update selected IDs whenever selected rows change
    setSelectedIds(new Set(selectedRows.map(row => row.runId)));
  }, [selectedRows]);

  const handleRemoveSelected = (rowToRemove: RollbackOptionsValue) => {
    setSelectedRows(prevSelected => {
      const newSelected = prevSelected.filter(
        item => item.runId !== rowToRemove.runId,
      );

      if (onSelectionChange) {
        onSelectionChange(newSelected);
      }

      return newSelected;
    });
  };

  const handleEnvironmentChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSelectedEnvironment(event.target.value as string);
  };

  const generatedColumns: TableColumn<RollbackOptionsValue>[] = useMemo(
    () => [
      {
        title: 'Trigger/module',
        customFilterAndSearch: (value, row: Partial<RollbackOptionsValue>) => {
          return (
            (row.triggerName?.toLowerCase().includes(value.toLowerCase()) ||
              row.runId?.toLowerCase().includes(value.toLowerCase()) ||
              row.module?.toLowerCase().includes(value.toLowerCase())) ??
            false
          );
        },
        render: (row: Partial<RollbackOptionsValue>) => (
          <>
            <Link href={row.url} target="_blank">
              {row.triggerName}
            </Link>
            <Typography variant="body2">/{row.module}</Typography>
          </>
        ),
      },
      {
        title: 'Tag/Version',
        customFilterAndSearch: (value, row: Partial<RollbackOptionsValue>) => {
          return (
            (row.ref?.toLowerCase().includes(value.toLowerCase()) ||
              row.tag?.toLowerCase().includes(value.toLowerCase()) ||
              row.shortSha?.toLowerCase().includes(value.toLowerCase())) ??
            false
          );
        },
        render: (row: Partial<RollbackOptionsValue>) => (
          <>
            <Typography variant="body2">
              {row.tag ? row.tag : row.ref}
            </Typography>
            <Typography variant="body2">{row.shortSha}</Typography>
          </>
        ),
      },
      {
        title: 'CreateTime',
        customFilterAndSearch: (value, row: Partial<RollbackOptionsValue>) => {
          const formattedDate = parseDateFromStringSeconds(row.createTime);
          return formattedDate.toLowerCase().includes(value.toLowerCase());
        },
        render: (row: Partial<RollbackOptionsValue>) => {
          const formattedDate = parseDateFromStringSeconds(row.createTime);
          return <Typography variant="body2">{formattedDate}</Typography>;
        },
      },
    ],
    [],
  );

  if (!rollbackOptions) {
    return null;
  }

  const environments = Object.keys(rollbackOptions);
  const dataToShow = selectedEnvironment
    ? rollbackOptions[selectedEnvironment] || []
    : [];

  const options = {
    selection: true,
    showSelectAllCheckbox: false,
    selectionProps: (rowData: RollbackOptionsValue) => ({
      checked: selectedIds.has(rowData.runId),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          // Add to selection if not already there
          if (!selectedIds.has(rowData.runId)) {
            const newSelection = [...selectedRows, rowData];
            setSelectedRows(newSelection);
            if (onSelectionChange) {
              onSelectionChange(newSelection);
            }
          }
        } else {
          // Remove from selection
          const newSelection = selectedRows.filter(
            row => row.runId !== rowData.runId,
          );
          setSelectedRows(newSelection);
          if (onSelectionChange) {
            onSelectionChange(newSelection);
          }
        }
      },
    }),
  };

  return (
    <>
      <Box mb={2}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="environment-selector-label">Environment</InputLabel>
          <Select
            labelId="environment-selector-label"
            id="environment-selector"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            label="Environment"
          >
            {environments.map(env => (
              <MenuItem key={env} value={env}>
                {env}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Table
        title={`${selectedEnvironment || 'selected environment'}`}
        subtitle="Select items to rollback"
        columns={generatedColumns}
        data={dataToShow}
        options={options}
      />

      {selectedRows.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Items ({selectedRows.length})
          </Typography>
          <Paper>
            <Box p={2} display="flex" flexWrap="wrap">
              {selectedRows.map(row => (
                <Chip
                  key={row.runId}
                  label={`${row.triggerName}${
                    row.module ? `/${row.module}` : ''
                  } - ${row.tag ?? row.ref ?? ''} (${row.shortSha})`}
                  onDelete={() => handleRemoveSelected(row)}
                  deleteIcon={<Close />}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};
